import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SUPPORTED_LANGS } from "@/i18n/i18n";
import { useT52 } from "@/i18n/v52";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NATIVE = { de: "Deutsch", fr: "Français", it: "Italiano", en: "English" };

export const initialsOf = (user) =>
  (user?.name || user?.email || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export function LangMenu({ testid = "lang-switcher-topbar", side = "bottom" }) {
  const { i18n } = useTranslation();
  const { changeLanguage } = useAuth();
  const { t52 } = useT52();
  const current = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : "de";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="f52-lang" data-testid={testid} aria-label={`${t52("language")}: ${NATIVE[current]}`}>
          <Globe aria-hidden="true" /><span style={{ textTransform: "uppercase" }}>{current}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side={side} className="w-44">
        {SUPPORTED_LANGS.map((lng) => (
          <DropdownMenuItem key={lng} data-testid={`lang-option-${lng}`} onClick={() => changeLanguage(lng)}
            className="flex cursor-pointer items-center justify-between text-sm">
            <span>{NATIVE[lng]}</span>{current === lng && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useLogout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); await logout(); navigate("/login"); };
  return [run, busy];
}

export function UserMenu() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { t52 } = useT52();
  const [logout, busy] = useLogout();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="f52-user" data-testid="user-menu-trigger" aria-label={t52("userMenu")}>
          <span className="f52-avatar" aria-hidden="true">{initialsOf(user)}</span>
          <span className="f52-user-meta"><span>{user?.name}</span><span className="f52-user-role">{user?.role_label}</span></span>
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm font-medium">{user?.name}</div>
          <div className="text-xs font-normal text-fundio-muted">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="logout-button" disabled={busy} onClick={logout} className="cursor-pointer text-status-danger focus:text-status-danger">
          <LogOut className="mr-2 h-4 w-4" /> {t("layout.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
